import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

//COMPONENTS
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";

//REDUX/UTILS
import { KeyValue } from "@/main/models/common";
import { getDefaultOptionDrop, getTranslateByKey, isNullUndefined, removeEmptyValues } from "@/main/services/utils";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { HolidaySaveRequest } from "@/main/models/request";
import { holidaysApi } from "@/main/api";
import { getOptionsStatus } from "@/main/services/data/data";

// STYLES/COMPONENTS LOCAL
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { Checkbox, FormControlLabel } from "@mui/material";
import DateMaskControlled from "@/main/components/shared/inputText/inputDateMaskControlled";
import { HolidaysResponse } from "@/main/models/response/holidaysResponse";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import * as S from "./styles";
import { KOGAreaButtons } from "@/main/components/shared/styles";

const INITIAL_FORM_STATE_DEFAULT = {
	country: null as KeyValue | null,
	dateHoliday: "",
	description: "",
	recurrent: true,
	status: null as KeyValue | null,
};
type IFormFiltersHoliday = typeof INITIAL_FORM_STATE_DEFAULT;

interface IModalHolidayProps {
	isOpenModal: boolean;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	itemHoliday?: HolidaysResponse;
	updateRowDataGrid?: (row: HolidaysResponse) => void;
	refreshSearchGrid?: (guidHoliday: string) => void;
	closeModal: () => void;
}

function ModalHoliday({
	itemHoliday,
	updateRowDataGrid = () => {},
	refreshSearchGrid = () => {},
	closeModal,
}: IModalHolidayProps) {
	const isEdit = !isNullUndefined(itemHoliday);
	// #region Hooks
	const dispatch = useDispatch();
	const methods = useForm<IFormFiltersHoliday>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE_DEFAULT,
	});
	const { control, handleSubmit, setValue, watch } = methods;
	const watcherRecurrent = watch("recurrent");
	// #endregion
	// #region reducers
	const { countriesUser: countryReducer } = useCountryReducer();
	const optionsStatus = getOptionsStatus();
	// #endregion

	// #region States
	const optionDefaultCountry = isEdit ? null : getDefaultOptionDrop(countryReducer.data);
	const optionDefaultStatus = isEdit ? null : optionsStatus.find((item) => item.key === StatusRegisterEnum.Active)!;
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	// #endregion

	function loadEditHoliday() {
		// Notation: this reset of react-hook-form set the default values of the form
		if (isNullUndefined(itemHoliday)) return;

		const formattedStatus = itemHoliday.active
			? optionsStatus.find((item) => item.key === StatusRegisterEnum.Active)
			: optionsStatus.find((item) => item.key == StatusRegisterEnum.Suspended);
		const backendForFrontend: IFormFiltersHoliday = {
			...INITIAL_FORM_STATE_DEFAULT,
			country: {
				key: itemHoliday.guidCountry,
				value: itemHoliday.countryName,
			},
			dateHoliday: itemHoliday?.date,
			description: itemHoliday?.description,
			recurrent: itemHoliday.recurring,
			status: formattedStatus as KeyValue,
		};

		methods.reset(backendForFrontend);
	}

	function handleBack() {
		closeModal();
	}
	// #region Core Functions
	function resetForm() {
		methods.reset();
		if (optionDefaultCountry) setValue("country", optionDefaultCountry);
		if (optionDefaultStatus) setValue("status", optionDefaultStatus);
	}
	// #endregion

	// #region Async functions
	async function onSubmitSave(data: IFormFiltersHoliday) {
		setIsLoadingSave(true);
		const request: HolidaySaveRequest = removeEmptyValues({
			...(isEdit && { guidHoliday: itemHoliday.guidHoliday }),
			guidCountry: data.country?.key,
			dateHoliday: data.dateHoliday,
			recurrent: data.recurrent,
			description: data.description,
			active: isNullUndefined(data.status) ? null : data.status?.key == StatusRegisterEnum.Active,
		});

		holidaysApi
			.saveHoliday(request)
			.then((response) => {
				toast.success(getTranslateByKey("information.savedSuccess"));
				if (!isEdit) {
					refreshSearchGrid(String(response.data.result.guidHoliday));
					resetForm();
				}
				if (isEdit) {
					methods.reset(data);
					updateRowDataGrid({
						guidHoliday: response.data.result.guidHoliday,
						guidCountry: response.data.result.guidCountry,
						countryName: response.data.result.countryName,
						description: response.data.result.description,
						date: response.data.result.date,
						recurring: response.data.result.recurring,
						recurringFormatted: response.data.result.recurringFormatted,
						active: response.data.result.active,
					});
				}
				closeModal();
				resetForm();
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}
	// #endregion

	// #region UseEffects
	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
	}, []);

	useEffect(() => {
		if (!isNullUndefined(itemHoliday)) {
			loadEditHoliday();
		}
	}, [JSON.stringify(itemHoliday)]);
	// #endregion

	//#region Disabled
	const disabledCountry = isNullUndefined(countryReducer.data) || countryReducer?.data?.length === 1;
	// #endregion

	return (
		<form onSubmit={handleSubmit(onSubmitSave)} noValidate>
			<S.Container>
				<KogAutoCompleteControlled
					id="id-holiday-select-country-register"
					nameCustom="country"
					label={getTranslateByKey("information.country")}
					isDefaultValueUnique
					valueDefaultKey={optionDefaultCountry?.key}
					control={control}
					rulesCustom={{ required: true }}
					setValueCustom={setValue}
					options={countryReducer.data}
					isLoading={countryReducer.isLoading}
					disabled={isEdit || disabledCountry}
					onChangeCustom={() => {}}
				/>

				<S.AreaInputNumber>
					<DateMaskControlled
						typeMask={watcherRecurrent ? "day/month" : "day/month/year"}
						required
						typeInput="filled"
						id="id-holiday-txt-dateholiday-register"
						name="dateHoliday"
						label={getTranslateByKey("information.date")}
						control={control}
					/>
					<Controller
						name="recurrent"
						control={control}
						render={({ field: { onChange, value } }) => (
							<S.AreaCheckbox>
								<FormControlLabel
									label={getTranslateByKey("information.recurrent")}
									control={
										<Checkbox
											color="primary"
											checked={value === true}
											onChange={onChange}
											id="id-register-checkBox-recurrent"
										/>
									}
								/>
							</S.AreaCheckbox>
						)}
					/>
				</S.AreaInputNumber>

				<KogInputTextControlledNew
					id="id-holiday-modal-txt-description"
					name="description"
					label={getTranslateByKey("information.description")}
					control={control}
					controllerProps={{ rules: { required: true, maxLength: 100 } }}
				/>
				<KogAutoCompleteControlled
					id="id-holiday-modal-select-status"
					nameCustom="status"
					label={getTranslateByKey("information.status")}
					valueDefaultKey={optionDefaultStatus?.key}
					control={control}
					rulesCustom={{ required: true }}
					setValueCustom={setValue}
					options={optionsStatus}
					onChangeCustom={() => {}}
				/>
				<KOGAreaButtons>
					<SecondaryButton id="id-holiday-btn-save" type="submit" isLoading={isLoadingSave}>
						{getTranslateByKey("information.save")}
					</SecondaryButton>
					<SecondaryButton id="id-holiday-btn-back" onClick={handleBack}>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
				</KOGAreaButtons>
			</S.Container>
		</form>
	);
}

function WrapperModalHoliday(props: IModalHolidayProps) {
	if (!props.isOpenModal) return null;

	const isEdit = !isNullUndefined(props.itemHoliday);
	const titleSaveModal = getTranslateByKey("information.registerholiday");
	const titleEditModal = getTranslateByKey("information.editholiday");
	const titleModal = isEdit ? titleEditModal : titleSaveModal;

	return (
		<KogModalSideComponent
			open={props.isOpenModal}
			shouldRenderOpen
			animateOnMount
			title={titleModal}
			closeModal={() => props.setIsOpenModal(false)}
		>
			<ModalHoliday {...props} />
		</KogModalSideComponent>
	);
}

export default WrapperModalHoliday;
